import React from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { FormValidation, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { useQueuedReport } from '~/hooks/apis/reporting/useQueuedReport';

import { getQueuedReportJobStatus, queueBQUnpaidBookings } from '~/services/ReportingService';

import QueuedReportControls from './QueuedReportControls';

interface FormData {
  date?: string;
  recordType?: string;
  paymentMethod?: string;
}

function validateForm(formData: FormData, errors: FormValidation): FormValidation {
  if (!formData.date) {
    errors.date.addError('Please enter date');
  }
  if (!formData.recordType) {
    errors.recordType.addError('Please choose a recordType option');
  }

  return errors;
}

const formSchema: RJSFSchema = {
  description: 'Please enter a date to export a list of all unpaid bookings payable on the selected date',
  type: 'object',
  properties: {
    date: {
      title: 'Select Date',
      type: 'string',
      format: 'date',
    },
    recordType: {
      title: 'Select Record Type',
      type: 'string',
      enum: ['default', 'rental'],
      default: 'default',
    },
    paymentMethod: {
      title: 'Payment Method',
      type: 'string',
      enum: ['All', 'EFT', 'VCC'],
      default: 'All',
    },
  },
};

const uiSchema = {
  date: {
    'ui:widget': 'date',
  },
};

export default function ExportBQUnpaidBookingsForm() {
  const report = useQueuedReport(
    'unpaid-bookings',
    queueBQUnpaidBookings,
    getQueuedReportJobStatus,
    'Unpaid Bookings report failed',
  );

  const handleSubmit = async (event: IChangeEvent<FormData>) => {
    if (report.isRunning || !event.formData.date || !event.formData.recordType) return;

    await report.start({
      date: event.formData.date,
      recordType: event.formData.recordType,
      paymentMethod: event.formData.paymentMethod,
    });
  };
  return (
    <Form
      schema={formSchema}
      uiSchema={uiSchema}
      onSubmit={handleSubmit}
      customValidate={validateForm}
      validator={validator}
      showErrorList={false}
      disabled={report.isRunning}
    >
      <QueuedReportControls
        {...report}
        submitLabel="Email Unpaid Bookings Report"
        lookingUpLabel="Looking up bookings…"
        generatingLabel="Generating spreadsheet..."
      />
    </Form>
  );
}
